import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Portal
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { apiScopes } from '../../../config/authConfig';
import { proposalApi } from '../services/proposalApi';
import { FormatType, TemplateVersion } from '../types';

interface PromptSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`prompt-tabpanel-${index}`}
    aria-labelledby={`prompt-tab-${index}`}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const FORMAT_LABELS: Record<FormatType, string> = {
  'full': 'Full Profile',
  'one-slider': '1 Bio per Slide',
  'intro': '4 Bios per Slide',
  'web-bio': 'Web Bio'
};

const PromptSettingsModal: React.FC<PromptSettingsModalProps> = ({ open, onClose }) => {
  const { instance, accounts } = useMsal();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [templates, setTemplates] = useState<Record<string, TemplateVersion[]>>({});
  const [currentTemplateInfo, setCurrentTemplateInfo] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const formatTypes: FormatType[] = ['full', 'one-slider', 'intro', 'web-bio'];
  const currentFormat = formatTypes[currentTab];

  // AUTHCFG-REVIEW-001 (Round 7 #122): this modal posts to
  // /api/proposal/* admin endpoints which the backend gates on the
  // Redactor API audience (api://AZURE_CLIENT_ID/access_as_user).
  // Acquiring a `User.Read` Graph token here meant every legitimate
  // admin call was rejected at the audience check. Reuse the shared
  // apiScopes from authConfig so this matches the rest of the
  // application's API-token flow.
  const getToken = async (): Promise<string> => {
    if (accounts.length === 0) {
      throw new Error('No accounts found');
    }

    const response = await instance.acquireTokenSilent({
      scopes: apiScopes,
      account: accounts[0]
    });

    return response.accessToken;
  };

  // Load prompts and templates on mount
  useEffect(() => {
    if (open) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();

      // Load prompts
      const promptsData = await proposalApi.getPrompts(token);
      setPrompts(promptsData);

      // Load template info for each format
      const templateInfo: Record<string, any> = {};
      const templateVersions: Record<string, TemplateVersion[]> = {};

      for (const format of formatTypes) {
        if (format !== 'full') {  // Full format doesn't use templates
          try {
            const info = await proposalApi.getCurrentTemplateInfo(format, token);
            templateInfo[format] = info;

            const versions = await proposalApi.listTemplateVersions(format, token);
            templateVersions[format] = versions;
          } catch (err) {
            console.warn(`No template info for ${format}:`, err);
          }
        }
      }

      setCurrentTemplateInfo(templateInfo);
      setTemplates(templateVersions);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePromptChange = (value: string) => {
    setPrompts({
      ...prompts,
      [currentFormat]: value
    });
  };

  const handleSavePrompt = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await proposalApi.updatePrompt(currentFormat, prompts[currentFormat], token);
      setSnackbar({
        open: true,
        message: `Prompt for ${FORMAT_LABELS[currentFormat]} saved successfully`,
        severity: 'success'
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save prompt');
      setSnackbar({
        open: true,
        message: 'Failed to save prompt',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.pptx')) {
      setError('File must be a .pptx PowerPoint file');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must not exceed 50MB');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await proposalApi.uploadTemplate(currentFormat, file, token);

      // Reload template info
      const info = await proposalApi.getCurrentTemplateInfo(currentFormat, token);
      const versions = await proposalApi.listTemplateVersions(currentFormat, token);

      setCurrentTemplateInfo({
        ...currentTemplateInfo,
        [currentFormat]: info
      });

      setTemplates({
        ...templates,
        [currentFormat]: versions
      });

      setSnackbar({
        open: true,
        message: `Template uploaded successfully: ${result.version}`,
        severity: 'success'
      });
    } catch (err: any) {
      setError(err.message || 'Failed to upload template');
      setSnackbar({
        open: true,
        message: 'Failed to upload template',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleSetTemplateVersion = async (versionName: string) => {
    try {
      const token = await getToken();
      await proposalApi.setTemplateVersion(currentFormat, versionName, token);

      // Reload template info
      const info = await proposalApi.getCurrentTemplateInfo(currentFormat, token);
      const versions = await proposalApi.listTemplateVersions(currentFormat, token);

      setCurrentTemplateInfo({
        ...currentTemplateInfo,
        [currentFormat]: info
      });

      setTemplates({
        ...templates,
        [currentFormat]: versions
      });

      setSnackbar({
        open: true,
        message: `Active version set to ${versionName}`,
        severity: 'success'
      });
    } catch (err: any) {
      setError(err.message || 'Failed to set template version');
    }
  };

  const handleInvalidateCache = async () => {
    try {
      const token = await getToken();
      await proposalApi.invalidatePromptCache(token);
      setSnackbar({
        open: true,
        message: 'Cache invalidated successfully',
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Failed to invalidate cache',
        severity: 'error'
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh', maxHeight: '90vh' } }}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Prompt & Template Settings</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleInvalidateCache}
              variant="outlined"
            >
              Clear Cache
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={currentTab}
                onChange={(_, value) => setCurrentTab(value)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                {formatTypes.map((format) => (
                  <Tab key={format} label={FORMAT_LABELS[format]} />
                ))}
              </Tabs>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {formatTypes.map((format, formatIndex) => (
                <TabPanel key={format} value={currentTab} index={formatIndex}>
                  <Box>
                    {/* Prompt Section */}
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Prompt Template
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={15}
                      value={prompts[format] || ''}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      placeholder="Enter prompt template..."
                      sx={{
                        mb: 2,
                        '& textarea': {
                          fontFamily: 'monospace',
                          fontSize: '0.875rem'
                        }
                      }}
                    />

                    {/* Template Section (not for 'full' format) */}
                    {format !== 'full' && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          PowerPoint Template
                        </Typography>

                        {/* Current Template Info */}
                        {currentTemplateInfo[format] && (
                          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Current Version
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {currentTemplateInfo[format].version || 'None'}
                            </Typography>
                            {currentTemplateInfo[format].uploaded_at && (
                              <Typography variant="caption" color="text.secondary">
                                Uploaded {new Date(currentTemplateInfo[format].uploaded_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Paper>
                        )}

                        {/* Upload Button */}
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                          disabled={uploading}
                          sx={{ mb: 2 }}
                        >
                          Upload New Template
                          <input
                            type="file"
                            hidden
                            accept=".pptx"
                            onChange={handleTemplateUpload}
                          />
                        </Button>

                        {/* Template Versions List */}
                        {templates[format] && templates[format].length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                              Available Versions
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                              <List dense>
                                {templates[format].map((version, idx) => (
                                  <React.Fragment key={version.version}>
                                    {idx > 0 && <Divider />}
                                    <ListItem>
                                      <ListItemText
                                        primary={version.version}
                                        secondary={`${version.size_mb}MB • Uploaded ${new Date(version.uploaded_at).toLocaleDateString()}`}
                                      />
                                      <ListItemSecondaryAction>
                                        {version.is_current ? (
                                          <CheckCircleIcon color="success" fontSize="small" />
                                        ) : (
                                          <Button
                                            size="small"
                                            onClick={() => handleSetTemplateVersion(version.version)}
                                          >
                                            Set Active
                                          </Button>
                                        )}
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  </React.Fragment>
                                ))}
                              </List>
                            </Paper>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </TabPanel>
              ))}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSavePrompt}
            variant="contained"
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save {FORMAT_LABELS[currentFormat]} Prompt
          </Button>
        </DialogActions>
      </Dialog>

      <Portal container={() => document.body}>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
        >
          <Alert
            severity={snackbar.severity as any}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ minWidth: '300px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default PromptSettingsModal;
